import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useReferral } from '@/hooks/useReferral';
import { Gift, Copy, Users, DollarSign, TrendingUp, Clock, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

export const ReferralDashboard = () => {
  const { referralCode, referralStats, referrals, earnings, loading, generateReferralCode } = useReferral();

  const copyReferralCode = () => {
    if (referralCode) {
      navigator.clipboard.writeText(referralCode);
      toast.success('Referral code copied to clipboard!');
    }
  };

  const copyReferralLink = () => {
    const link = `${window.location.origin}/auth?ref=${referralCode}`;
    navigator.clipboard.writeText(link);
    toast.success('Referral link copied to clipboard!');
  };

  if (loading) {
    return (
      <Card className="clean-card">
        <CardContent className="p-6">
          <p className="text-center text-muted-foreground">Loading referral data...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <Card className="clean-card gradient-purple text-white">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-white/10 rounded-lg">
              <Gift className="h-6 w-6" />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold mb-2">Referral Program</h2>
              <p className="text-white/90 mb-4">
                Earn 1% of your referrals' spending for their first 3 months on the platform!
              </p>
              
              {!referralCode ? (
                <Button 
                  onClick={generateReferralCode}
                  variant="secondary"
                  className="rounded-full"
                >
                  Generate Your Referral Code
                </Button>
              ) : (
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <Input 
                      value={referralCode}
                      readOnly
                      className="bg-white/10 border-white/20 text-white font-mono text-lg"
                    />
                    <Button 
                      onClick={copyReferralCode}
                      variant="secondary"
                      size="icon"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                  <Button 
                    onClick={copyReferralLink}
                    variant="outline"
                    className="w-full border-white/20 text-white hover:bg-white/10"
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Copy Referral Link
                  </Button>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="clean-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-primary/10 rounded-lg">
                <Users className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Referrals</p>
                <p className="text-2xl font-bold text-foreground">{referralStats.totalReferrals}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="clean-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-500/10 rounded-lg">
                <TrendingUp className="h-5 w-5 text-green-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Active Referrals</p>
                <p className="text-2xl font-bold text-foreground">{referralStats.activeReferrals}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="clean-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-accent/10 rounded-lg">
                <DollarSign className="h-5 w-5 text-accent" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Earnings</p>
                <p className="text-2xl font-bold text-foreground">{referralStats.totalEarnings} coins</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for Referrals and Earnings */}
      <Tabs defaultValue="referrals" className="space-y-4">
        <TabsList className="grid w-full grid-cols-2 bg-white border">
          <TabsTrigger value="referrals">My Referrals</TabsTrigger>
          <TabsTrigger value="earnings">Earnings History</TabsTrigger>
        </TabsList>

        <TabsContent value="referrals">
          <Card className="clean-card">
            <CardHeader>
              <CardTitle>Your Referrals</CardTitle>
            </CardHeader>
            <CardContent>
              {referrals.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="h-12 w-12 mx-auto text-muted-foreground/50 mb-2" />
                  <p className="text-muted-foreground">No referrals yet</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Share your code to start earning!
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {referrals.map((referral) => {
                    const isActive = referral.status === 'active' && new Date(referral.expires_at) > new Date();
                    const daysLeft = isActive 
                      ? Math.ceil((new Date(referral.expires_at).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
                      : 0;

                    return (
                      <div 
                        key={referral.id}
                        className="flex items-center gap-3 p-4 border rounded-xl bg-white"
                      >
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={referral.profiles?.avatar_url} />
                          <AvatarFallback className="bg-primary/10 text-primary">
                            {referral.profiles?.display_name?.[0] || 'U'}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-foreground">
                            {referral.profiles?.display_name || 'Unknown User'}
                          </p>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <span>Joined {formatDistanceToNow(new Date(referral.created_at), { addSuffix: true })}</span>
                            {isActive && (
                              <>
                                <span>•</span>
                                <span className="flex items-center gap-1">
                                  <Clock className="h-3 w-3" />
                                  {daysLeft} days left
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-foreground">{referral.total_earnings} coins</p>
                          <p className="text-xs text-muted-foreground">earned</p>
                        </div>
                        <Badge 
                          variant={isActive ? 'default' : 'secondary'}
                          className="ml-2"
                        >
                          {isActive ? (
                            <>
                              <TrendingUp className="h-3 w-3 mr-1" />
                              Active
                            </>
                          ) : (
                            <>
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Expired
                            </>
                          )}
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="earnings">
          <Card className="clean-card">
            <CardHeader>
              <CardTitle>Earnings History</CardTitle>
            </CardHeader>
            <CardContent>
              {earnings.length === 0 ? (
                <div className="text-center py-8">
                  <DollarSign className="h-12 w-12 mx-auto text-muted-foreground/50 mb-2" />
                  <p className="text-muted-foreground">No earnings yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {earnings.map((earning) => (
                    <div 
                      key={earning.id}
                      className="flex items-center gap-3 p-3 border rounded-lg bg-white hover:bg-muted/50 transition-colors"
                    >
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={earning.profiles?.avatar_url} />
                        <AvatarFallback className="bg-primary/10 text-primary text-xs">
                          {earning.profiles?.display_name?.[0] || 'U'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground">
                          {earning.profiles?.display_name || 'Unknown User'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Spent {earning.referee_spent} coins
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-green-500">+{earning.amount}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(earning.created_at), { addSuffix: true })}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
